import { useFormControllerContext } from "@/contexts";
import {
    type FieldValues,
    type UseControllerProps,
    useController as useFieldController,
} from "react-hook-form";
import { type BaseSelectProps, Select } from "./select";

type FormSelectProps<T extends FieldValues> = BaseSelectProps & {
    fieldName: UseControllerProps<T>["name"];
};

const RegisteredSelect = <T extends FieldValues>({
    fieldName,
    items,
    ...props
}: FormSelectProps<T>) => {
    const formController = useFormControllerContext();

    const fieldController = useFieldController<T>({
        control: formController.control,
        name: fieldName,
    });

    if (!items) {
        return null;
    }

    const errorMessage = fieldName
        ?.split(".")
        .reduce((o, i) => o?.[i], formController.formState.errors)?.message;

    return (
        <Select
            name={fieldName}
            value={fieldController.field.value}
            onChange={fieldController.field.onChange}
            errorMessage={errorMessage}
            isInvalid={!!errorMessage}
            items={items || []}
            {...props}
        />
    );
};

export const FormSelect = <T extends FieldValues>({
    shouldRegister = true,
    ...otherProps
}: FormSelectProps<T> & { shouldRegister?: boolean }) => {
    return shouldRegister ? (
        <RegisteredSelect {...otherProps} />
    ) : (
        <Select {...otherProps} />
    );
};
